import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { announce, newSince, practicePush } from '../lib/desktopAlerts.js';

/**
 * ehr#252 - the header bell: the signed-in person's unread notices from Alerts Hub (the
 * platform's staff-notice store; e.g. "Form shared with you"), read through the EHR API as that
 * person. A "Form shared with you" row opens the shared form inside the EHR; any other row opens
 * where the hub points (its open link also marks it read). The footer keeps the org-wide
 * Notification log one click away, which is what the bell opened before.
 * ehr#540: it also lists the person's unread CRITICAL and HIGH alerts (a risk flag, a late
 * cancel, a note or task overdue) on top, in red and amber, and counts them.
 */

export interface HubNotice {
  id: string;
  type: string;
  title: string;
  desc: string;
  clientName: string | null;
  contactId: string | null;
  submissionId: string | null;
  ts: string | null;
  openUrl: string;
  /** alertshub#5 / ehr#540: a CRITICAL alert (a risk flag, a late cancel), listed first and in red. */
  critical?: boolean;
  /** ehr#540: a HIGH alert (a task or note overdue), listed next and marked. */
  high?: boolean;
}

/** ehr#540: the row's class and its priority tag (null for an ordinary notice). */
export function noticeLevel(n: Pick<HubNotice, 'critical' | 'high'>): {
  className: string;
  tag: string | null;
} {
  if (n.critical) return { className: 'nb-item nb-item-critical', tag: 'Critical' };
  if (n.high) return { className: 'nb-item nb-item-high', tag: 'High' };
  return { className: 'nb-item', tag: null };
}
export interface HubNoticeList {
  available: boolean;
  total: number;
  notices: HubNotice[];
  hubUrl: string;
}

const POLL_MS = 60_000;
const UNREACHABLE: HubNoticeList = {
  available: false,
  total: 0,
  notices: [],
  hubUrl: 'https://alerts.credifyfast.com',
};

/** "Just now" / "5 min ago" / "2 h ago" / "Oct 6". */
export function noticeAge(iso: string | null, now = Date.now()): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '';
  const min = Math.floor((now - t) / 60_000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ago`;
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const BELL_ICON = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

export function NoticeBell({
  overdueTasks,
  onOpenLog,
  onOpenSharedForm,
}: {
  /** The existing overdue-tasks dot (#36) still lights the bell. */
  overdueTasks: boolean;
  onOpenLog: () => void;
  onOpenSharedForm: (contactId: string, submissionId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<HubNoticeList | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // alertshub#8: the ids already seen, so only notices new since the last look chime / push.
  const seen = useRef<Set<string> | null>(null);
  const load = useCallback(() => {
    api
      .get<HubNoticeList>('/hub-notices')
      .then((l) => {
        setList(l);
        if (!l.available) return;
        const fresh = newSince(seen.current, l.notices);
        seen.current = new Set([...(seen.current ?? []), ...l.notices.map((n) => n.id)]);
        if (fresh.length)
          void practicePush().then((push) =>
            announce(
              fresh.map((n) => ({ title: n.title, body: n.desc, tag: `credify-notice-${n.id}` })),
              push,
            ),
          );
      })
      .catch(() => setList((prev) => prev ?? { ...UNREACHABLE }));
  }, []);
  useEffect(() => {
    load();
    const t = window.setInterval(load, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);
  // Close on an outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = list?.available ? list.total : 0;
  const notices = list?.available ? list.notices : [];
  const drop = (id: string) =>
    setList((prev) =>
      prev
        ? {
            ...prev,
            total: Math.max(0, prev.total - 1),
            notices: prev.notices.filter((n) => n.id !== id),
          }
        : prev,
    );
  const openNotice = (n: HubNotice) => {
    drop(n.id);
    setOpen(false);
    if (n.type === 'FORM_SHARED' && n.contactId && n.submissionId) {
      void api.post(`/hub-notices/${n.id}/read`).catch(() => undefined);
      onOpenSharedForm(n.contactId, n.submissionId);
      return;
    }
    // The hub's open link marks it read and redirects to where the notice points.
    window.open(n.openUrl, '_blank', 'noopener,noreferrer');
  };
  const label = unread
    ? `Notifications, ${unread} unread`
    : overdueTasks
      ? 'Notifications, tasks overdue'
      : 'Notifications';

  return (
    <div className="nb-wrap" ref={wrapRef}>
      <button
        className="header-bell"
        aria-label={label}
        title="Notifications"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          if (!open) load();
          setOpen((o) => !o);
        }}
      >
        {BELL_ICON}
        {unread > 0 ? (
          <span className="nb-count">{unread > 9 ? '9+' : unread}</span>
        ) : overdueTasks ? (
          <span className="header-bell-dot" />
        ) : null}
      </button>
      {open && (
        <div className="nb-panel" role="dialog" aria-label="Notifications">
          <div className="nb-head">
            <span className="nb-title">Notifications</span>
            {unread > 0 && <span className="nb-sub">{unread} unread</span>}
          </div>
          {!list ? (
            <div className="nb-empty">Loading…</div>
          ) : !list.available ? (
            <div className="nb-empty">Alerts Hub couldn&apos;t be reached. Try again shortly.</div>
          ) : notices.length === 0 ? (
            <div className="nb-empty">You&apos;re all caught up.</div>
          ) : (
            <ul className="nb-list">
              {notices.map((n) => {
                const level = noticeLevel(n);
                return (
                  <li key={n.id}>
                    <button type="button" className={level.className} onClick={() => openNotice(n)}>
                      <span className="nb-item-title">
                        {level.tag && <span className="nb-item-tag">{level.tag}</span>}
                        {n.title}
                      </span>
                      <span className="nb-item-desc">{n.desc}</span>
                      <span className="nb-item-meta">
                        {[n.clientName, noticeAge(n.ts)].filter(Boolean).join(' · ')}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {list?.available && unread > notices.length && (
            <div className="nb-more">{unread - notices.length} more in Alerts Hub</div>
          )}
          <div className="nb-foot">
            <a
              className="nb-link"
              href={list?.hubUrl ?? 'https://alerts.credifyfast.com'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
            >
              Open Alerts Hub
            </a>
            <button
              type="button"
              className="nb-link"
              onClick={() => {
                setOpen(false);
                onOpenLog();
              }}
            >
              Notification log
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
